import { z } from 'zod';
import { IdSchema } from '../../ids.js';
import { GoogleEventColorIdSchema } from '../../google/eventColors.js';

export const RecurrenceUnitSchema = z.enum(['day', 'week', 'month', 'year']);
export type RecurrenceUnit = z.infer<typeof RecurrenceUnitSchema>;

/**
 * 'completion' reschedules from the day the task was finished — the chore
 * example (dog cleaned 8/10 -> next due 11/10). 'schedule' reschedules from
 * the ORIGINAL due date, so a fixed obligation paid late does not drift
 * (spec §6).
 */
export const RecurrenceAnchorSchema = z.enum(['completion', 'schedule']);
export type RecurrenceAnchor = z.infer<typeof RecurrenceAnchorSchema>;

export const RecurrenceSchema = z.object({
  every: z.number().int().positive(),
  unit: RecurrenceUnitSchema,
  anchor: RecurrenceAnchorSchema,
});
export type Recurrence = z.infer<typeof RecurrenceSchema>;

export const NotifyPrefsSchema = z.object({
  inApp: z.boolean().default(true),
  email: z.boolean().default(true),
});
export type NotifyPrefs = z.infer<typeof NotifyPrefsSchema>;

/** 24-hour "HH:mm", read as a wall-clock time in `America/New_York` — see `nagStart`. */
export const TimeOfDaySchema = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/);

/**
 * `'ok'` covers both "synced successfully" and "sync not applicable" (the
 * board or this task has it turned off) — there is no `'disabled'` state
 * because nothing downstream needs to distinguish those two; both mean
 * there is nothing to show the user. `'pending'`/`'error'` are what
 * `reconcilePendingCalendarSyncs` (api/src/google/taskSync.ts) retries.
 */
export const CalendarSyncStateSchema = z.enum(['ok', 'pending', 'error']);
export type CalendarSyncState = z.infer<typeof CalendarSyncStateSchema>;

export const TaskSchema = z.object({
  id: IdSchema,
  householdId: IdSchema,
  boardId: IdSchema,
  title: z.string().min(1).max(160),
  description: z.string().max(2000).default(''),
  dueAt: z.string(),
  recurrence: RecurrenceSchema.nullable(),
  leadTimeDays: z.number().int().nonnegative().default(0),
  /** When notifications begin on their start day, Eastern time. `null` means the default, midnight. */
  notifyTimeOfDay: TimeOfDaySchema.nullable(),
  /** How often a still-outstanding task re-nags, in hours. `null` means the recurrence-based default — see `effectiveRenotifyIntervalHours`. */
  renotifyIntervalHours: z.number().int().positive().max(24 * 30).nullable(),
  notify: NotifyPrefsSchema,
  /** Household member (by `sub`) this task is targeted at. `null` means unassigned — the whole household is notified, same as before this field existed (see api/src/reminder.ts). */
  assigneeId: z.string().nullable().default(null),
  status: z.enum(['active', 'completed']),
  /** Set by snooze; governs external delivery pacing only — see this plan's design note. */
  snoozedUntil: z.string().nullable(),
  dismissed: z.boolean(),
  notifyAfter: z.string().nullable(),
  lastCompletedAt: z.string().nullable(),
  lastCompletedBy: z.string().nullable(),
  /** Whether this task should mirror onto Google Calendar — chosen per-task, not board-wide. */
  syncToCalendar: z.boolean().default(false),
  /** Which of the household's connected Google calendars this task syncs to. Required once `syncToCalendar` is true, but not enforced at the schema level — an unset value while sync is on is a real, surfaced misconfiguration (see `syncError`), not silently ignored. */
  calendarId: z.string().nullable().default(null),
  /** One of Google Calendar's fixed event colors (see `GOOGLE_EVENT_COLORS`) to set on the synced event. `null` leaves it unset, so the event takes the target calendar's own default color. Kept independent of `syncToCalendar` so a choice survives toggling sync off and back on. */
  colorId: GoogleEventColorIdSchema.nullable().default(null),
  /** Both null until the first successful sync; identify the one Google event mirroring this task's *current* occurrence — see FEATURE_ANALYSIS.md's Phase 3, "Occurrences, not recurring events". `googleCalendarId` is the calendar the live event actually lives in, which can briefly lag `calendarId` right after the user changes their selection, until the next sync moves it. */
  googleEventId: z.string().nullable(),
  googleCalendarId: z.string().nullable(),
  syncState: CalendarSyncStateSchema,
  syncError: z.string().nullable(),
  createdBy: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
  version: z.number().int().nonnegative(),
});
export type Task = z.infer<typeof TaskSchema>;

export const CreateTaskSchema = z.object({
  title: z.string().min(1).max(160),
  description: z.string().max(2000).default(''),
  dueAt: z.string(),
  recurrence: RecurrenceSchema.nullable().default(null),
  leadTimeDays: z.number().int().nonnegative().default(0),
  notifyTimeOfDay: TimeOfDaySchema.nullable().default(null),
  renotifyIntervalHours: z.number().int().positive().max(24 * 30).nullable().default(null),
  notify: NotifyPrefsSchema.default({ inApp: true, email: true }),
  assigneeId: z.string().nullable().default(null),
  syncToCalendar: z.boolean().default(false),
  calendarId: z.string().nullable().default(null),
  colorId: GoogleEventColorIdSchema.nullable().default(null),
});
export type CreateTaskInput = z.infer<typeof CreateTaskSchema>;

export const UpdateTaskSchema = CreateTaskSchema.extend({
  /** The version the client last read. A mismatch means someone else wrote. */
  version: z.number().int().nonnegative(),
});
export type UpdateTaskInput = z.infer<typeof UpdateTaskSchema>;

export const SnoozeTaskSchema = z.object({
  /** `0` clears the snooze — notifications resume on the next sweep. */
  hours: z.number().nonnegative().max(24 * 30).default(24),
});
export type SnoozeTaskInput = z.infer<typeof SnoozeTaskSchema>;

export const TaskAuditActionSchema = z.enum([
  'created',
  'updated',
  'completed',
  'uncompleted',
  'snoozed',
  'snooze_cleared',
  'dismissed',
]);
export type TaskAuditAction = z.infer<typeof TaskAuditActionSchema>;

/** One edited field on an `'updated'` audit entry. Values are display strings (see `diffTaskFields`), except `assigneeId`, which is the member's `sub` for the client to resolve. */
export const TaskAuditChangeSchema = z.object({
  field: z.string(),
  from: z.string().nullable(),
  to: z.string().nullable(),
});
export type TaskAuditChange = z.infer<typeof TaskAuditChangeSchema>;

export const TaskAuditEntrySchema = z.object({
  id: z.string(),
  taskId: IdSchema,
  at: z.string(),
  /** A member's `sub`, `device:<id>` for a wall dashboard, or `email-action` for an email link. */
  actor: z.string(),
  action: TaskAuditActionSchema,
  /** Snooze length, for `'snoozed'` entries. */
  hours: z.number().nullable(),
  /** Per-field edits, for `'updated'` entries. */
  changes: z.array(TaskAuditChangeSchema),
});
export type TaskAuditEntry = z.infer<typeof TaskAuditEntrySchema>;

/**
 * A tasks board's `Board.config`. Empty for now — Google Calendar sync
 * (formerly configured here as `googleSync: { enabled, calendarId }`) is
 * chosen per-task instead (see `TaskSchema.syncToCalendar`/`calendarId`),
 * since a household's tasks commonly belong to different people who each
 * want their own tasks on their own calendar, not one calendar for the
 * whole board. Kept as a real schema (not removed outright) since
 * `registerBoardType` requires every board type to have a `configSchema`.
 */
export const TasksBoardConfigSchema = z.object({});
export type TasksBoardConfig = z.infer<typeof TasksBoardConfigSchema>;
